/**
 * CodingPanel — simplified v2 UX.
 *
 * Renders ICD-10 diagnoses and CPT services with per-row selection checkboxes
 * and a Copy button. A single "Push to EHR" button acts on the checked subset.
 * Skeleton rows are shown while the coding agent is running.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  apiClient,
  CodingResult,
  CodingDiagnosis,
  CodingService,
  CptStatus,
  CoverageStatus,
  ChangeFromOriginal,
  QueryAnswerInput,
  BundleDxCode,
  BundlePxCode,
} from '../services/api';
import { QueryPanel } from './QueryPanel';

interface CodingPanelProps {
  sessionId: string;
  onCodingComplete?: (sessionId: string) => void;
  refreshTick?: number;
}

// ── Bundle-shape adapters ─────────────────────────────────────────────────────
// The two-phase bundle returns dx.final_diagnoses[] / px.services[] entries
// (no item_id). Normalize them into the legacy row shapes so the existing row
// renderers work for both old stored runs and new bundle runs.

function isBundleShape(result: CodingResult): boolean {
  if (result.dx || result.px) return true;
  const first: any = (result.diagnoses || [])[0] || (result.services || [])[0];
  return !!first && first.item_id === undefined;
}

function normalizeCodingResult(result: CodingResult): { dxRows: CodingDiagnosis[]; svcRows: CodingService[] } {
  if (!isBundleShape(result)) {
    return {
      dxRows: (result.diagnoses || []) as CodingDiagnosis[],
      svcRows: (result.services || []) as CodingService[],
    };
  }
  const dxSrc = ((result.dx && result.dx.final_diagnoses) || result.diagnoses || []) as BundleDxCode[];
  const pxSrc = ((result.px && result.px.services) || result.services || []) as BundlePxCode[];

  const dxRows: CodingDiagnosis[] = dxSrc.map((d, i) => ({
    item_id: `dx_${d.code || i}`,
    code: d.code || '',
    description: d.description || '',
    is_primary: !!d.is_primary,
    change_from_original: d.is_new ? 'added' : 'existing',
    detailed_reasoning: [
      d.justification || '',
      (d.guidelines || []).length ? `Guidelines: ${(d.guidelines || []).join('; ')}` : '',
    ].filter(Boolean).join('\n'),
  }));

  const svcRows: CodingService[] = pxSrc.map((s: any, i) => ({
    item_id: `svc_${s.serial_number || s.code || i}`,
    service_id: String(s.serial_number ?? ''),
    code: s.code || '',
    name: s.name || s.description || '',
    cpt_status: (s.cpt_status as CptStatus) ?? null,
    cpt_suggestion: s.cpt_suggestion ?? null,
    cpt_reason: s.cpt_reason ?? null,
    em_coding_basis: s.em_coding_basis ?? null,
    mdm_complexity: s.mdm_complexity ?? null,
    modifier_suggestion:
      s.modifier_suggestion
      || ((s.modifiers || []).length ? (s.modifiers as string[]).join(', ') : null),
    information_source: s.instructions ?? s.cpt_reason ?? undefined,
    missing_documentation: s.missing_documentation ?? undefined,
  }));

  return { dxRows, svcRows };
}

export function CodingPanel({ sessionId, onCodingComplete, refreshTick = 0 }: CodingPanelProps) {
  const [result, setResult] = useState<CodingResult | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selectedForEhr, setSelectedForEhr] = useState<Set<string>>(new Set());
  const [copyTooltip, setCopyTooltip] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushToast, setPushToast] = useState<string | null>(null);
  const pushToastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Bumped by "Retry coding" to re-arm the poll after a re-code is requested.
  const [retryTick, setRetryTick] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);
  // Non-null after a Re-run: poll results whose coding_runs_count is <= this
  // are the previous run and must not be shown as final.
  const minRunsAfterRetryRef = useRef<number | null>(null);
  // Phase-2 resolve state.
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Default-check codes that are ready for EHR push (exclude REVIEW / INCORRECT).
  // Only diagnoses that actually carry a code are selected — code-less drafts
  // must not be pushed to the EHR with an empty code.
  const autoSelectForEhr = useCallback((data: CodingResult) => {
    const { dxRows, svcRows } = normalizeCodingResult(data);
    const ids = new Set<string>([
      ...dxRows.filter((d) => d.code && d.code !== '—').map((d) => d.item_id),
      ...svcRows
        .filter((s) => s.code && s.code !== '—' && s.cpt_status !== 'REVIEW' && s.cpt_status !== 'INCORRECT')
        .map((s) => s.item_id),
    ]);
    setSelectedForEhr(ids);
  }, []);

  // Poll until status is not 'pending'
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const data = await apiClient.getCodingResults(sessionId);
        if (cancelled) return;
        // After a Re-run, a result whose runs-count hasn't grown past the
        // pre-retry value is the OLD run (a completed result read before the
        // new run stored anything). Showing it as Final would stop the poll
        // on stale codes — keep polling until the new run reports.
        if (
          minRunsAfterRetryRef.current !== null
          && data.status !== 'pending'
          && (data.coding_runs_count ?? 0) <= minRunsAfterRetryRef.current
        ) {
          timer = setTimeout(poll, 3000);
          return;
        }
        if (data.status !== 'pending') minRunsAfterRetryRef.current = null;
        setResult(data);
        if (data.status === 'pending') {
          timer = setTimeout(poll, 3000);
        } else if (data.status === 'needs_resolution' || data.has_open_queries) {
          // Draft codes only — the clinician must answer the queries first.
          // No EHR auto-selection while the result is provisional, but DO
          // light the Billing-tab badge: this is the state that needs action.
          onCodingComplete?.(sessionId);
        } else {
          autoSelectForEhr(data);
          onCodingComplete?.(sessionId);
        }
      } catch (e: any) {
        if (cancelled) return;
        // 404 = the session no longer exists (e.g. an empty session was cleaned
        // up). Stop polling instead of hammering the endpoint forever.
        if (e?.status === 404) return;
        timer = setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId, refreshTick, retryTick, autoSelectForEhr, onCodingComplete]);

  // Phase 2: submit query answers → /coding/resolve; handle cascade rounds.
  const handleResolve = useCallback(async (answers: QueryAnswerInput[]) => {
    if (resolveBusy || !sessionId) return;
    setResolveBusy(true);
    setResolveError(null);
    try {
      const updated = await apiClient.submitQueryAnswers(sessionId, answers);
      setResult(updated);
      if (updated.status !== 'needs_resolution' && !updated.has_open_queries) {
        autoSelectForEhr(updated);
        onCodingComplete?.(sessionId);
      }
    } catch (e: any) {
      setResolveError(e?.message || 'Could not submit answers — please try again.');
    } finally {
      setResolveBusy(false);
    }
  }, [resolveBusy, sessionId, autoSelectForEhr, onCodingComplete]);

  const handleRetryCoding = useCallback(async () => {
    // Ref guard: `retrying` is state and lags a render — a fast double-click
    // passed it and fired two retry POSTs.
    if (retryingRef.current || !sessionId) return;
    retryingRef.current = true;
    setRetrying(true);
    try {
      // Results at or below this runs-count are the old run — the poll skips
      // them until the new run has stored its entry.
      minRunsAfterRetryRef.current = result?.coding_runs_count ?? 0;
      await apiClient.retryCoding(sessionId);
      // Show pending immediately and re-arm the poll.
      setResult({ status: 'pending', diagnoses: [], services: [] } as CodingResult);
      setRetryTick((t) => t + 1);
    } catch {
      // Leave the current state; the button stays available to try again.
      minRunsAfterRetryRef.current = null;
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  }, [sessionId, result]);

  const toggleExpand = useCallback((key: string) => {
    setExpanded((prev) => (prev === key ? null : key));
  }, []);

  const toggleSelect = useCallback((itemId: string) => {
    setSelectedForEhr((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const handleCopy = useCallback((code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopyTooltip(code);
      setTimeout(() => setCopyTooltip(null), 1500);
    });
  }, []);

  const handlePushToEhr = useCallback(async () => {
    if (pushBusy || !result) return;
    setPushBusy(true);
    const { dxRows: dxList, svcRows: svcList } = normalizeCodingResult(result);
    const codes = [
      ...dxList.filter((d) => selectedForEhr.has(d.item_id) && d.code && d.code !== '—').map((d) => ({ code: d.code, kind: 'dx' as const })),
      ...svcList.filter((s) => selectedForEhr.has(s.item_id)).map((s) => ({ code: s.code, kind: 'px' as const })),
    ];
    try {
      await apiClient.pushCodesToEhr(sessionId, codes);
    } catch {
      // stub always resolves
    } finally {
      setPushBusy(false);
      if (pushToastTimer.current) clearTimeout(pushToastTimer.current);
      setPushToast('EHR not connected');
      pushToastTimer.current = setTimeout(() => setPushToast(null), 3000);
    }
  }, [pushBusy, result, sessionId, selectedForEhr]);

  // No result yet (initial load) or coding pending — show empty sections
  if (!result || result.status === 'pending') {
    const isPending = result?.status === 'pending';
    return (
      <div className="coding-panel" style={panelStyle}>
        <div className="coding-panel-section" style={{ padding: '0 12px 16px' }}>
          <div style={sectionHeader}>Diagnoses (ICD-10)</div>
          {isPending ? <SkeletonRows count={3} /> : (
            <p style={emptyHintStyle}>Diagnoses will appear here after you end the note.</p>
          )}
          <div style={{ ...sectionHeader, marginTop: 14 }}>Services (CPT / HCPCS)</div>
          {isPending ? <SkeletonRows count={3} /> : (
            <p style={emptyHintStyle}>Services will appear here after you end the note.</p>
          )}
        </div>
      </div>
    );
  }

  if (result.status === 'error') {
    return (
      <div className="coding-panel" style={panelStyle}>
        <div className="coding-panel-section" style={{ padding: '0 12px 16px' }}>
          <p style={emptyHintStyle}>Coding didn't complete — the coding service was unavailable.</p>
          {result.analysis && <p style={emptyHintStyle}>{result.analysis}</p>}
          <button onClick={handleRetryCoding} disabled={retrying} style={retryBtnStyle(retrying)}>
            {retrying ? 'Retrying…' : 'Retry coding'}
          </button>
        </div>
      </div>
    );
  }

  const { dxRows: dxList, svcRows: svcList } = normalizeCodingResult(result);
  const needsResolution = result.status === 'needs_resolution' || !!result.has_open_queries;
  const degraded = result.status === 'degraded';

  // Terminal "no codes" state: either copilot reported no_content, or it
  // returned a final result with no diagnoses and no services (e.g. a visit
  // with no billable orders). Show a clear message rather than an empty
  // "Billing codes" header with no rows and no action button.
  if (!needsResolution
      && (result.status === 'no_content' || (dxList.length === 0 && svcList.length === 0))) {
    return (
      <div className="coding-panel" style={panelStyle}>
        <div className="coding-panel-section" style={{ padding: '0 12px 16px' }}>
          <div style={sectionHeader}>Diagnoses (ICD-10)</div>
          <p style={emptyHintStyle}>No billing codes were generated for this note.</p>
          {result.analysis && <p style={emptyHintStyle}>{result.analysis}</p>}
          <button onClick={handleRetryCoding} disabled={retrying} style={retryBtnStyle(retrying)}>
            {retrying ? 'Retrying…' : 'Retry coding'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="coding-panel" style={panelStyle}>
      <div style={headerStyle}>
        {needsResolution ? 'Billing codes — Clarification stage' : 'Billing codes — Final'}
      </div>

      {/* The note moved on after these codes were made (a long visit's final
          note landing late, a typed addition, a feedback rewrite). Nothing
          re-codes on its own — the clinician decides. */}
      {result.codes_stale && (
        <div style={staleBannerStyle}>
          <div>The note changed after these codes were generated.</div>
          <button onClick={handleRetryCoding} disabled={retrying} style={retryBtnStyle(retrying)}>
            {retrying ? 'Re-coding…' : 'Re-run coding'}
          </button>
        </div>
      )}

      {degraded && (result.dx_error || result.px_error) && (
        <div style={degradedBannerStyle}>
          {result.dx_error && <div>⚠ Diagnosis engine: {result.dx_error}</div>}
          {result.px_error && <div>⚠ Service engine: {result.px_error}</div>}
          <div style={{ marginTop: 4, fontWeight: 400 }}>
            Showing the results that completed. You can retry coding to re-run both.
          </div>
        </div>
      )}

      {result.encounter_summary && (
        <div style={analysisStyle}>{result.encounter_summary}</div>
      )}

      {result.analysis && (
        <div style={analysisStyle}>{result.analysis}</div>
      )}

      {result.inpatient_summary && (
        <div style={inpatientSummaryStyle}>
          <strong>Inpatient summary:</strong> {result.inpatient_summary}
        </div>
      )}

      {needsResolution && (
        <QueryPanel
          queries={result.queries || []}
          iteration={result.iteration || 1}
          busy={resolveBusy}
          onSubmit={handleResolve}
          errorText={resolveError}
        />
      )}

      {needsResolution && (dxList.length > 0 || svcList.length > 0) && (
        <div style={draftNoticeStyle}>
          Draft codes — provisional until the clarifications above are finalized.
        </div>
      )}

      <div className="coding-panel-section" style={{ padding: '0 12px 12px' }}>
        {dxList.length > 0 && (
          <section>
            <div style={sectionHeader}>Diagnoses (ICD-10)</div>
            {dxList.map((dx) => (
              <DiagnosisRow
                key={dx.item_id}
                dx={dx}
                selected={selectedForEhr.has(dx.item_id)}
                onSelect={() => toggleSelect(dx.item_id)}
                expanded={expanded === dx.item_id}
                onToggleExpand={() => toggleExpand(dx.item_id)}
                onCopy={() => handleCopy(dx.code)}
                copyActive={copyTooltip === dx.code}
              />
            ))}
          </section>
        )}

        {svcList.length > 0 && (
          <section style={{ marginTop: 14 }}>
            <div style={sectionHeader}>Services (CPT / HCPCS)</div>
            {svcList.map((svc) => (
              <ServiceRow
                key={svc.item_id}
                svc={svc}
                selected={selectedForEhr.has(svc.item_id)}
                onSelect={() => toggleSelect(svc.item_id)}
                expanded={expanded === svc.item_id}
                onToggleExpand={() => toggleExpand(svc.item_id)}
                onCopy={() => handleCopy(svc.code)}
                copyActive={copyTooltip === svc.code}
              />
            ))}
          </section>
        )}

        {!needsResolution && (dxList.length > 0 || svcList.length > 0) && (
          <div style={ehrRowStyle}>
            <button
              onClick={handlePushToEhr}
              disabled={pushBusy || selectedForEhr.size === 0}
              style={pushBtnStyle(pushBusy || selectedForEhr.size === 0)}
            >
              {pushBusy ? 'Pushing…' : 'Push to EHR'}
            </button>
            {pushToast && (
              <span style={toastStyle}>{pushToast}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Skeleton rows
// ─────────────────────────────────────────────────────────────────────────────

function SkeletonRows({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skel-row">
          <div className="skel-pulse" style={{ width: 40, height: 12, borderRadius: 4, background: '#e5e7eb', marginRight: 8 }} />
          <div className="skel-pulse" style={{ width: 64, height: 12, borderRadius: 4, background: '#e5e7eb', marginRight: 8 }} />
          <div className="skel-pulse" style={{ flex: 1, height: 12, borderRadius: 4, background: '#e5e7eb' }} />
        </div>
      ))}
    </>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Diagnosis row
// ─────────────────────────────────────────────────────────────────────────────

interface DiagnosisRowProps {
  dx: CodingDiagnosis;
  selected: boolean;
  onSelect: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onCopy: () => void;
  copyActive: boolean;
}

function DiagnosisRow({ dx, selected, onSelect, expanded, onToggleExpand, onCopy, copyActive }: DiagnosisRowProps) {
  return (
    <div style={rowStyle(false)}>
      <div style={rowHeaderStyle}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          style={checkboxStyle}
          title="Include in EHR push"
        />

        <span style={primaryBadgeStyle(dx.is_primary)}>
          {dx.is_primary ? 'PRI' : 'SEC'}
        </span>

        <span style={codeStyle}>{dx.code || '—'}</span>
        <span style={descStyle}>{dx.description || ''}</span>

        {dx.hcc_category != null && (
          <span style={hccBadgeStyle} title={dx.hcc_description || ''}>
            HCC-{dx.hcc_category}
          </span>
        )}

        {dx.change_from_original && (
          <span style={changeBadgeStyle(dx.change_from_original)}>
            {changeLabel(dx.change_from_original)}
          </span>
        )}

        {dx.poa_indicator && (
          <span style={smallPillStyle('#f3f4f6', '#374151')}>POA {dx.poa_indicator}</span>
        )}

        {dx.detailed_reasoning && (
          <button onClick={onToggleExpand} style={whyButtonStyle}>
            {expanded ? 'hide' : 'why?'}
          </button>
        )}

        <button onClick={onCopy} style={copyBtnStyle} title="Copy code">
          {copyActive ? 'Copied' : 'Copy'}
        </button>
      </div>

      {expanded && dx.detailed_reasoning && (
        <div style={reasoningStyle}>{dx.detailed_reasoning}</div>
      )}

      {hasRealValue(dx.alternate_candidates) && (
        <div style={alternatesStyle}>
          <span style={{ fontWeight: 600 }}>Alternates:</span> {dx.alternate_candidates}
        </div>
      )}

      {hasRealValue(dx.missing_documentation) && (
        <div style={warningStripStyle}>
          ⚠ Missing documentation: {dx.missing_documentation}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Service row
// ─────────────────────────────────────────────────────────────────────────────

interface ServiceRowProps {
  svc: CodingService;
  selected: boolean;
  onSelect: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  onCopy: () => void;
  copyActive: boolean;
}

const NEEDS_REVIEW_STATUSES: CptStatus[] = ['REVIEW', 'INCORRECT'];

function ServiceRow({ svc, selected, onSelect, expanded, onToggleExpand, onCopy, copyActive }: ServiceRowProps) {
  const statusStyle = cptStatusStyle(svc.cpt_status ?? null);
  const coverageStyleVal = coverageChipStyle(svc.coverage ?? null);
  const hasNoCode = !svc.code || svc.code === '—';
  const needsReview = hasNoCode || (svc.cpt_status != null && NEEDS_REVIEW_STATUSES.includes(svc.cpt_status));

  return (
    <div style={{ ...rowStyle(false), opacity: needsReview ? 0.72 : 1 }}>
      <div style={rowHeaderStyle}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          style={checkboxStyle}
          title={needsReview ? 'Flagged for review — include at your judgment' : 'Include in EHR push'}
        />
        {needsReview && (
          <span
            title="Needs review before pushing to EHR"
            style={{ width: 16, height: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >
            ⚠
          </span>
        )}

        <span style={serialBadgeStyle}>{svc.service_id}</span>

        <span style={codeStyle}>{svc.code || '—'}</span>
        <span style={descStyle}>{svc.name || ''}</span>

        {svc.cpt_status && (
          <span style={{ ...smallPillStyle(statusStyle.bg, statusStyle.fg), fontWeight: 600 }}>
            {svc.cpt_status.replace(/_/g, ' ')}
          </span>
        )}

        {svc.cpt_suggestion
          && svc.cpt_status !== 'CORRECT'
          && svc.cpt_status !== 'CLINICIAN_MODIFIED'
          && svc.cpt_status !== 'RESOLVED_FROM_DESCRIPTION'
          && svc.cpt_status !== 'RESOLVED_FROM_DESCRIPTION_FALLBACK' && (
          <span style={{ ...smallPillStyle('#eef2ff', '#4338ca'), fontWeight: 600 }}>
            → {svc.cpt_suggestion}
          </span>
        )}

        {svc.em_coding_basis && svc.em_coding_basis !== 'N/A' && (
          <span style={smallPillStyle('#f5f3ff', '#6d28d9')}>
            {svc.em_coding_basis}{svc.mdm_complexity && svc.mdm_complexity !== 'N/A' ? `: ${svc.mdm_complexity}` : ''}
          </span>
        )}

        {svc.coverage && (
          <span style={{ ...smallPillStyle(coverageStyleVal.bg, coverageStyleVal.fg) }}>
            {coverageLabel(svc.coverage)}
          </span>
        )}

        {(svc.cpt_reason || svc.guideline_reference || svc.information_source) && (
          <button onClick={onToggleExpand} style={whyButtonStyle}>
            {expanded ? 'hide' : 'details'}
          </button>
        )}

        <button onClick={onCopy} style={copyBtnStyle} title="Copy code">
          {copyActive ? 'Copied' : 'Copy'}
        </button>
      </div>

      {svc.supporting_diagnoses && (
        <div style={metaLineStyle}>
          <span style={metaLabelStyle}>Supporting Dx:</span> {svc.supporting_diagnoses}
        </div>
      )}

      {svc.modifier_suggestion && svc.modifier_suggestion !== 'None' && (
        <div style={metaLineStyle}>
          <span style={metaLabelStyle}>Modifier:</span> {svc.modifier_suggestion}
        </div>
      )}

      {svc.bundling_alert && svc.bundling_alert !== 'No bundling conflicts identified' && svc.bundling_alert !== 'None' && (
        <div style={{ ...metaLineStyle, color: '#c2410c' }}>
          <span style={metaLabelStyle}>⚠ Bundling:</span> {svc.bundling_alert}
        </div>
      )}

      {expanded && (
        <div style={reasoningStyle}>
          {svc.cpt_reason && (
            <div style={{ marginBottom: 4 }}>
              <strong>CPT:</strong> {svc.cpt_reason}
            </div>
          )}
          {svc.information_source && (
            <div style={{ marginBottom: 4 }}>
              <strong>Justification:</strong> {svc.information_source}
            </div>
          )}
          {svc.coverage_rejection_reason && (
            <div style={{ marginBottom: 4, color: '#b91c1c' }}>
              <strong>Coverage:</strong> {svc.coverage_rejection_reason}
            </div>
          )}
          {svc.guideline_reference && (
            <div style={{ marginBottom: 4, color: '#7c3aed' }}>
              📋 <strong>{svc.guideline_reference}</strong>
              {svc.guideline_impact && <>: {svc.guideline_impact}</>}
            </div>
          )}
        </div>
      )}

      {hasRealValue(svc.missing_documentation) && (
        <div style={warningStripStyle}>
          ⚠ Missing documentation: {svc.missing_documentation}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Value helpers
// ─────────────────────────────────────────────────────────────────────────────

function hasRealValue(v: unknown): v is string {
  if (v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s.length > 0 && s !== 'none' && s !== 'null' && s !== 'n/a' && s !== 'na';
}


// ─────────────────────────────────────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────────────────────────────────────

function cptStatusStyle(s: CptStatus): { bg: string; fg: string } {
  switch (s) {
    case 'CORRECT':                            return { bg: '#dcfce7', fg: '#15803d' };
    case 'INCORRECT':
    case 'UPCODED':                            return { bg: '#fee2e2', fg: '#b91c1c' };
    case 'DOWNCODED':                          return { bg: '#fef9c3', fg: '#854d0e' };
    case 'OPTIMIZE':
    case 'UNBUNDLED':                          return { bg: '#ffedd5', fg: '#c2410c' };
    case 'RESOLVED_FROM_DESCRIPTION':          return { bg: '#dbeafe', fg: '#1d4ed8' };
    case 'RESOLVED_FROM_DESCRIPTION_FALLBACK': return { bg: '#e0e7ff', fg: '#4338ca' };
    case 'CLINICIAN_MODIFIED':                 return { bg: '#dbeafe', fg: '#1d4ed8' };
    case 'REVIEW':
    default:                                    return { bg: '#f3f4f6', fg: '#374151' };
  }
}

function coverageChipStyle(c: CoverageStatus): { bg: string; fg: string } {
  switch (c) {
    case 'covered':                return { bg: '#dcfce7', fg: '#15803d' };
    case 'not_covered':            return { bg: '#fee2e2', fg: '#b91c1c' };
    case 'requires_authorization': return { bg: '#fef9c3', fg: '#854d0e' };
    case 'pending':                return { bg: '#f3f4f6', fg: '#374151' };
    case 'N/A':
    default:                       return { bg: '#f9fafb', fg: '#6b7280' };
  }
}

function coverageLabel(c: CoverageStatus): string {
  switch (c) {
    case 'covered':                return '✓ covered';
    case 'not_covered':            return '✗ not covered';
    case 'requires_authorization': return '🔒 auth required';
    case 'pending':                return '⏳ pending';
    case 'N/A':                    return 'N/A';
    default:                       return String(c);
  }
}

function changeLabel(c: ChangeFromOriginal): string {
  switch (c) {
    case 'added':    return 'NEW';
    case 'modified': return 'CHANGED';
    case 'removed':  return 'REMOVED';
    case 'existing': return '';
    default:         return '';
  }
}

function changeBadgeStyle(c: ChangeFromOriginal): React.CSSProperties {
  switch (c) {
    case 'added':    return smallPillStyle('#dcfce7', '#15803d');
    case 'modified': return smallPillStyle('#fef9c3', '#854d0e');
    case 'removed':  return smallPillStyle('#fee2e2', '#b91c1c');
    default:         return { display: 'none' };
  }
}

function primaryBadgeStyle(isPrimary: boolean): React.CSSProperties {
  return {
    ...smallPillStyle(isPrimary ? '#e0e7ff' : '#f3f4f6', isPrimary ? '#4338ca' : '#6b7280'),
    fontWeight: 700,
    minWidth: 30,
    textAlign: 'center',
  };
}

const panelStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  background: '#fff',
};

const headerStyle: React.CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3f4f6',
  fontWeight: 700,
  fontSize: 13,
  color: '#111827',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const analysisStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#f9fafb',
  borderBottom: '1px solid #f3f4f6',
  fontSize: 12,
  lineHeight: 1.5,
  color: '#374151',
};

const inpatientSummaryStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#eef2ff',
  borderBottom: '1px solid #e0e7ff',
  fontSize: 12,
  color: '#3730a3',
};

const degradedBannerStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#fef2f2',
  borderBottom: '1px solid #fecaca',
  fontSize: 12,
  lineHeight: 1.5,
  color: '#b91c1c',
  fontWeight: 600,
};

const staleBannerStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#fffbeb',
  borderBottom: '1px solid #fde68a',
  fontSize: 12,
  lineHeight: 1.5,
  color: '#92400e',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',   // narrow frame: the Re-run button drops below the text
  gap: 8,
};

const draftNoticeStyle: React.CSSProperties = {
  margin: '0 12px 8px',
  padding: '6px 10px',
  background: '#f9fafb',
  border: '1px dashed #d1d5db',
  borderRadius: 8,
  fontSize: 11.5,
  color: '#6b7280',
};

const sectionHeader: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#6b7280',
  marginBottom: 6,
  marginTop: 10,
};

const emptyHintStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#9ca3af',
  margin: '4px 0 10px',
  fontStyle: 'italic',
};

function retryBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    marginTop: 10,
    padding: '8px 16px',
    borderRadius: 6,
    border: 'none',
    background: disabled ? '#e5e7eb' : '#1e293b',
    color: disabled ? '#6b7280' : '#fff',
    fontWeight: 600,
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
  };
}

function rowStyle(_needsAttention: boolean): React.CSSProperties {
  return {
    marginBottom: 8,
    background: '#f9fafb',
    borderRadius: 6,
    padding: '8px 10px',
    border: '1px solid transparent',
  };
}

const rowHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 6,
};

const checkboxStyle: React.CSSProperties = {
  width: 14,
  height: 14,
  cursor: 'pointer',
  flexShrink: 0,
};

const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 12,
  fontWeight: 700,
  color: '#1d4ed8',
};

const descStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#374151',
  flex: 1,
  minWidth: 120,
};

const hccBadgeStyle: React.CSSProperties = {
  ...smallPillStyle('#f3e8ff', '#6b21a8'),
  fontWeight: 700,
};

const serialBadgeStyle: React.CSSProperties = {
  ...smallPillStyle('#f3f4f6', '#6b7280'),
  fontFamily: 'monospace',
  fontWeight: 700,
  minWidth: 24,
  textAlign: 'center',
};

const copyBtnStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#1e293b',
  background: 'none',
  border: '1px solid #bfdbfe',
  borderRadius: 3,
  padding: '1px 6px',
  cursor: 'pointer',
  marginLeft: 'auto',
  flexShrink: 0,
};

const ehrRowStyle: React.CSSProperties = {
  marginTop: 16,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',   // the "pushed" toast wraps under the button when narrow
  gap: 10,
};

function pushBtnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '7px 16px',
    borderRadius: 6,
    border: 'none',
    background: disabled ? '#9ca3af' : '#1e293b',
    color: '#fff',
    fontWeight: 600,
    fontSize: 13,
    cursor: disabled ? 'default' : 'pointer',
  };
}

const toastStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
  background: '#f3f4f6',
  padding: '4px 10px',
  borderRadius: 6,
};

function smallPillStyle(bg: string, fg: string): React.CSSProperties {
  return {
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 10,
    background: bg,
    color: fg,
    whiteSpace: 'nowrap',
  };
}

const whyButtonStyle: React.CSSProperties = {
  fontSize: 10,
  color: '#6b7280',
  background: 'none',
  border: '1px solid #d1d5db',
  borderRadius: 3,
  padding: '0 4px',
  cursor: 'pointer',
};

const reasoningStyle: React.CSSProperties = {
  marginTop: 6,
  fontSize: 11,
  color: '#4b5563',
  lineHeight: 1.5,
  background: '#fff',
  padding: '6px 8px',
  borderRadius: 4,
  border: '1px solid #f3f4f6',
};

// Display-only "Alternates: CODE: description, ..." line (mirrors the copilot
// app). No selection in this phase.
const alternatesStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  color: '#6b7280',
  lineHeight: 1.5,
};

const metaLineStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 11,
  color: '#4b5563',
};

const metaLabelStyle: React.CSSProperties = {
  fontWeight: 600,
  color: '#374151',
};

const warningStripStyle: React.CSSProperties = {
  marginTop: 6,
  padding: '4px 8px',
  background: '#fef3c7',
  color: '#92400e',
  fontSize: 11,
  borderRadius: 4,
};
